import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  Clock,
  Database,
  Download,
  Eye,
  FileText,
  Filter,
  KeyRound,
  Loader2,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  User,
  Wifi,
  X,
  XCircle,
} from 'lucide-react';

import { auditAPI } from '../services/api';
import PermissionGate from '../components/PermissionGate';
import AuditEvidenceDrawer from '../components/AuditEvidenceDrawer';

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INDIA_TIME_ZONE = 'Asia/Kolkata';
const IST_OFFSET_MINUTES = 330;

const DEFAULT_FILTERS = {
  search: '',
  severity: '',
  event_category: '',
  event_type: '',
  event_action: '',
  actor_user_id: '',
  admin_email: '',
  resource_type: '',
  resource_id: '',
  outcome_status: '',
  start_date: '',
  end_date: '',
  time_preset: '24h',
};

const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'auth', label: 'Authentication' },
  { value: 'rbac', label: 'Access control' },
  { value: 'pii', label: 'PII access' },
  { value: 'document', label: 'Documents' },
  { value: 'ropa', label: 'ROPA' },
  { value: 'revocation', label: 'Retention / revocation' },
  { value: 'microsoft', label: 'Microsoft / Entra' },
  { value: 'security', label: 'Security monitoring' },
  { value: 'system', label: 'System' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All severities' },
  { value: 'INFO', label: 'Info' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'CRITICAL', label: 'Critical' },
];

const OUTCOME_OPTIONS = [
  { value: '', label: 'All outcomes' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'denied', label: 'Denied' },
  { value: 'error', label: 'Error' },
];

const RESOURCE_TYPE_OPTIONS = [
  { value: '', label: 'All resources' },
  { value: 'admin_auth', label: 'Admin auth' },
  { value: 'admin_user', label: 'Admin user' },
  { value: 'group', label: 'Group' },
  { value: 'permission', label: 'Permission' },
  { value: 'pii_record', label: 'PII record' },
  { value: 'document', label: 'Document' },
  { value: 'view_session', label: 'View session' },
  { value: 'ropa_activity', label: 'ROPA activity' },
  { value: 'revocation_run', label: 'Revocation run' },
  { value: 'microsoft_group', label: 'Microsoft group' },
];

const TIME_PRESETS = [
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom' },
  { value: '', label: 'All time' },
];

const EVENT_CATALOG = [
  { category: 'auth', value: 'auth.login.failed', label: 'Login failed', action: 'login_failed' },
  { category: 'auth', value: 'auth.otp.generated', label: 'OTP generated', action: 'otp_generated' },
  { category: 'auth', value: 'auth.otp.failed', label: 'OTP failed', action: 'otp_verify_failed' },
  { category: 'auth', value: 'auth.login.success', label: 'Login success', action: 'login_success' },
  { category: 'auth', value: 'auth.logout', label: 'Logout', action: 'logout' },
  { category: 'auth', value: 'auth.logout.failed', label: 'Logout failed', action: 'logout_failed' },
  { category: 'rbac', value: 'rbac.group.created', label: 'Group created', action: 'group_created' },
  { category: 'rbac', value: 'rbac.group.updated', label: 'Group updated', action: 'group_updated' },
  { category: 'rbac', value: 'rbac.group.deactivated', label: 'Group deactivated', action: 'group_deactivated' },
  { category: 'rbac', value: 'rbac.group.permissions_updated', label: 'Permissions updated', action: 'permissions_updated' },
  { category: 'rbac', value: 'rbac.group.member_added', label: 'Group member added', action: 'member_added' },
  { category: 'rbac', value: 'rbac.group.member_removed', label: 'Group member removed', action: 'member_removed' },
  { category: 'rbac', value: 'rbac.admin_user.created', label: 'Admin user created', action: 'admin_user_created' },
  { category: 'rbac', value: 'rbac.admin_user.status_changed', label: 'Admin status changed', action: 'admin_user_status_changed' },
  { category: 'pii', value: 'pii.record.view_token_generated', label: 'PII viewed by admin', action: 'view_token_generated' },
  { category: 'pii', value: 'pii.record.view_token_used', label: 'PII view opened', action: 'view_token_used' },
  { category: 'pii', value: 'PII_RECORD_ACCESSED', label: 'Legacy PII record accessed', action: 'view' },
  { category: 'pii', value: 'BULK_ACCESS_REQUESTED', label: 'Legacy bulk access requested', action: 'bulk_access' },
  { category: 'document', value: 'document.view_token_generated', label: 'Document viewed by admin', action: 'view_token_generated' },
  { category: 'document', value: 'document.view_token_used', label: 'Document view opened', action: 'view_token_used' },
  { category: 'document', value: 'DOCUMENT_VIEWED', label: 'Legacy document viewed', action: 'document_viewed' },
  { category: 'document', value: 'VIEW_SESSION_EVENT', label: 'Document viewed by admin', action: 'view_session' },
  { category: 'ropa', value: 'ropa.activity.created', label: 'ROPA activity created', action: 'activity_created' },
  { category: 'ropa', value: 'ropa.activity.updated', label: 'ROPA activity updated', action: 'activity_updated' },
  { category: 'ropa', value: 'ropa.activity.deleted', label: 'ROPA activity deleted', action: 'activity_deleted' },
  { category: 'ropa', value: 'ropa.activity.auto_mapped', label: 'ROPA auto-map executed', action: 'auto_mapped' },
  { category: 'revocation', value: 'REVOCATION_RUN_TRIGGERED', label: 'Revocation run triggered', action: 'revocation_run_triggered' },
  { category: 'security', value: 'SUSPICIOUS_ACTIVITY_DETECTED', label: 'Suspicious activity', action: 'suspicious_activity' },
  { category: 'microsoft', value: 'microsoft.login.started', label: 'Microsoft login started', action: 'login_started' },
  { category: 'microsoft', value: 'microsoft.login.success', label: 'Microsoft login success', action: 'login_success' },
  { category: 'microsoft', value: 'microsoft.login.failed', label: 'Microsoft login failed', action: 'login_failed' },
  { category: 'microsoft', value: 'microsoft.groups.synced', label: 'Microsoft groups synced', action: 'groups_synced' },
];

const QUICK_FILTERS = [
  { id: 'review', label: 'Needs review', icon: AlertTriangle, apply: { severity: 'WARNING', outcome_status: '' } },
  { id: 'critical', label: 'Critical', icon: ShieldAlert, apply: { severity: 'CRITICAL' } },
  { id: 'auth_failed', label: 'Failed logins', icon: KeyRound, apply: { event_category: 'auth', outcome_status: 'failed' } },
  { id: 'pii', label: 'PII access', icon: Database, apply: { event_category: 'pii' } },
  { id: 'rbac', label: 'RBAC changes', icon: ShieldCheck, apply: { event_category: 'rbac' } },
  { id: 'legacy', label: 'Legacy events', icon: FileText, apply: { search: 'legacy_raw_proto' } },
];

const HIGH_RISK_FIELD_RULES = [
  { keys: ['aadhaar', 'aadhar'], risk: 'critical' },
  { keys: ['pan'], risk: 'critical' },
  { keys: ['passport'], risk: 'critical' },
  { keys: ['bank', 'account', 'ifsc'], risk: 'critical' },
  { keys: ['salary', 'income', 'tax'], risk: 'high' },
  { keys: ['dob', 'date_of_birth', 'birth'], risk: 'high' },
  { keys: ['address', 'postal', 'pincode', 'pin_code'], risk: 'high' },
  { keys: ['phone', 'mobile'], risk: 'medium' },
  { keys: ['email'], risk: 'medium' },
];

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function valueOrDash(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function formatRelative(value) {
  if (!value) return '—';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return formatDate(value);
  const diffMin = Math.round((Date.now() - timestamp) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.round(diffHr / 24)}d ago`;
}

function truncateMiddle(value, start = 12, end = 8) {
  if (!value) return '';
  const text = String(value);
  if (text.length <= start + end + 3) return text;
  return `${text.slice(0, start)}...${text.slice(-end)}`;
}

function toIndiaInputValue(date) {
  if (!date) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: INDIA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function indiaInputToUtcIso(value) {
  if (!value) return '';

  const text = String(value).trim();

  if (/Z$|[+-]\d{2}:?\d{2}$/.test(text)) {
    return text;
  }

  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);

  if (!match) return text;

  const [, year, month, day, hour, minute] = match.map(Number);
  const utcMs = Date.UTC(year, month - 1, day, hour, minute) - IST_OFFSET_MINUTES * 60 * 1000;

  return new Date(utcMs).toISOString();
}

function normalizeDateFilterParam(key, value) {
  if (!value) return value;

  if (key === 'start_date' || key === 'end_date') {
    return indiaInputToUtcIso(value);
  }

  return value;
}

function getPresetRange(preset) {
  if (!preset || preset === 'custom') return {};

  const now = new Date();
  let start = new Date(now);

  if (preset === '1h') start = new Date(now.getTime() - 60 * 60 * 1000);
  if (preset === '24h') start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (preset === '7d') start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  if (preset === '30d') start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  return {
    start_date: toIndiaInputValue(start),
    end_date: toIndiaInputValue(now),
  };
}

function normalizeToken(value) {
  return String(value || '').trim().toLowerCase().replaceAll('_', '.');
}

function inferCategory(event) {
  const category = String(event?.event_category || '').toLowerCase();
  if (category) return category;
  const eventType = String(event?.event_type || '').toLowerCase();
  const action = String(event?.event_action || event?.action || '').toLowerCase();
  const resourceType = String(event?.resource_type || '').toLowerCase();
  if (eventType.startsWith('auth.') || action.includes('login') || action.includes('otp') || action.includes('logout')) return 'auth';
  if (eventType.startsWith('rbac.') || resourceType.includes('group') || resourceType.includes('permission') || resourceType.includes('admin_user')) return 'rbac';
  if (eventType.startsWith('pii.') || eventType.includes('pii') || resourceType.includes('pii')) return 'pii';
  if (eventType.startsWith('document.') || eventType.includes('document') || eventType.includes('view_session')) return 'document';
  if (eventType.startsWith('ropa.') || resourceType.includes('ropa')) return 'ropa';
  if (eventType.includes('revocation') || resourceType.includes('revocation')) return 'revocation';
  if (eventType.startsWith('microsoft.') || eventType.includes('entra') || eventType.includes('azure_ad')) return 'microsoft';
  if (eventType.includes('suspicious') || eventType.includes('security')) return 'security';
  return 'system';
}

function getCategoryMeta(category) {
  return CATEGORY_OPTIONS.find((item) => item.value === category) || CATEGORY_OPTIONS[CATEGORY_OPTIONS.length - 1];
}

function getEventCatalogItem(eventType) {
  const normalized = normalizeToken(eventType);
  return EVENT_CATALOG.find((item) => normalizeToken(item.value) === normalized);
}

function getBusinessEventLabel(event) {
  const eventType = String(event?.event_type || '').toLowerCase();
  const action = String(event?.event_action || event?.action || '').toLowerCase();
  const resourceType = String(event?.resource_type || '').toLowerCase();

  if (eventType === 'pii.record.view_token_generated' || action === 'view_token_generated' && resourceType.includes('pii')) {
    return 'PII viewed by admin';
  }

  if (eventType === 'pii.record.view_token_used' || action === 'view_token_used' && resourceType.includes('pii')) {
    return 'PII view opened';
  }

  if (eventType === 'document.view_token_generated' || action === 'view_token_generated' && resourceType.includes('document')) {
    return 'Document viewed by admin';
  }

  if (eventType === 'document.view_token_used' || action === 'view_token_used' && resourceType.includes('document')) {
    return 'Document view opened';
  }

  if (eventType === 'view_session_event' || eventType.includes('view_session')) {
    return 'Document viewed by admin';
  }

  return '';
}

function getEventLabel(event) {
  const businessLabel = getBusinessEventLabel(event);
  if (businessLabel) return businessLabel;

  const catalog = getEventCatalogItem(event?.event_type);
  if (catalog) return catalog.label;

  return String(event?.event_type || 'Audit event').replaceAll('_', ' ').replaceAll('.', ' › ');
}

function getEventActionLabel(event) {
  const action = event?.event_action || event?.action || '';
  const catalog = EVENT_CATALOG.find((item) => item.action === action);
  if (catalog) return catalog.label;
  return String(action || '—').replaceAll('_', ' ');
}

function getOutcome(event) {
  return String(event?.outcome_status || event?.outcome?.status || 'unknown').toLowerCase();
}

function getEvidenceSource(event) {
  return event?.evidence_source || event?.metadata?.snapshot_schema || (event?.snapshot_available ? 'audit_db_snapshot' : 'legacy_raw_proto');
}

function hasSnapshot(event) {
  return event?.snapshot_available === true || getEvidenceSource(event) === 'audit_db_snapshot';
}

function parseMaybeJson(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function toArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => String(item || '').trim()).filter(Boolean);
  if (typeof value === 'string') {
    if (!value.trim()) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((item) => String(item || '').trim()).filter(Boolean);
    } catch {
      // fallback below
    }
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [String(value).trim()].filter(Boolean);
}

function unique(values) {
  return Array.from(new Set(toArray(values)));
}

function getEventGovernance(event) {
  const evidenceSnapshot = event?.evidence_snapshot || event?.audit_snapshot?.evidence_snapshot || {};
  const nestedGovernance = evidenceSnapshot.access_governance || evidenceSnapshot.governance || {};

  return (
    event?.access_governance ||
    nestedGovernance ||
    evidenceSnapshot ||
    parseMaybeJson(event?.access_reason) ||
    parseMaybeJson(event?.reason) ||
    {}
  );
}

function getEventFieldAccess(event) {
  const governance = getEventGovernance(event);
  const evidenceSnapshot = event?.evidence_snapshot || event?.audit_snapshot?.evidence_snapshot || {};
  const summary =
    event?.field_access_summary ||
    event?.field_access ||
    governance?.field_access ||
    evidenceSnapshot?.field_access ||
    {};

  return {
    viewMode: governance.view_mode || summary.view_mode || '',
    purposeCode: governance.purpose_code || summary.purpose_code || '',
    accessReason: governance.access_reason || summary.access_reason || '',
    justification: governance.justification || summary.justification || '',
    requested: unique(governance.requested_fields || summary.requested_fields || event?.requested_fields || []),
    returned: unique(governance.returned_fields || summary.returned_fields || event?.returned_fields || event?.accessed_fields || []),
    masked: unique(governance.masked_fields || summary.masked_fields || event?.masked_fields || []),
    denied: unique(governance.denied_fields || summary.denied_fields || event?.denied_fields || []),
  };
}

function normalizeFieldName(value) {
  return String(value || '').trim().toLowerCase().replaceAll(' ', '_');
}

function getFieldRisk(fieldName) {
  const normalized = normalizeFieldName(fieldName);
  return HIGH_RISK_FIELD_RULES.find((rule) => rule.keys.some((key) => normalized.includes(key))) || { risk: 'low' };
}

function formatFieldList(fields, max = 3) {
  const safe = unique(fields);
  if (!safe.length) return '';
  const visible = safe.slice(0, max).join(', ');
  const remaining = safe.length - max;
  return remaining > 0 ? `${visible} +${remaining} more` : visible;
}

function getAccessRisk(event) {
  const category = inferCategory(event);
  const fieldAccess = getEventFieldAccess(event);
  const outcome = getOutcome(event);
  const severity = String(event?.severity || '').toUpperCase();

  if (outcome === 'failed' || outcome === 'error') return { level: 'warning', label: 'Failed operation', message: 'The audit event completed with a failed outcome.' };
  if (outcome === 'denied') return { level: 'warning', label: 'Access denied', message: 'The request was denied by policy or authorization.' };

  if (category === 'pii') {
    const returned = fieldAccess.returned;
    const denied = fieldAccess.denied;
    const viewMode = String(fieldAccess.viewMode || '').toLowerCase();
    const returnedRisks = returned.map((field) => ({ field, ...getFieldRisk(field) }));
    const critical = returnedRisks.filter((item) => item.risk === 'critical');
    const high = returnedRisks.filter((item) => item.risk === 'high');
    if (critical.length) return { level: 'critical', label: 'High-risk PII returned', message: `${critical.length} critical field(s) returned: ${formatFieldList(critical.map((item) => item.field))}.` };
    if (high.length) return { level: 'warning', label: 'Sensitive PII returned', message: `${high.length} sensitive field(s) returned: ${formatFieldList(high.map((item) => item.field))}.` };
    if (denied.length) return { level: 'warning', label: 'Policy denial', message: `${denied.length} requested field(s) denied: ${formatFieldList(denied)}.` };
    if (viewMode === 'full' && returned.length) return { level: 'warning', label: 'Full PII view', message: `Full view returned ${returned.length} selected field(s).` };
  }

  if (severity === 'CRITICAL') return { level: 'critical', label: 'Critical', message: 'The audit service marked this event as critical.' };
  if (severity === 'WARNING') return { level: 'warning', label: 'Needs review', message: 'The audit service marked this event for review.' };
  return { level: 'ok', label: 'Recorded', message: 'Audit event recorded with no immediate warning.' };
}

function riskClasses(level) {
  if (level === 'critical') return { badge: 'bg-red-50 text-red-700 ring-red-100', row: 'border-l-red-500', panel: 'border-red-200 bg-red-50 text-red-800', dot: 'bg-red-500' };
  if (level === 'warning') return { badge: 'bg-orange-50 text-orange-700 ring-orange-100', row: 'border-l-orange-500', panel: 'border-orange-200 bg-orange-50 text-orange-800', dot: 'bg-orange-500' };
  if (level === 'info') return { badge: 'bg-amber-50 text-amber-700 ring-amber-100', row: 'border-l-amber-400', panel: 'border-amber-200 bg-amber-50 text-amber-800', dot: 'bg-amber-500' };
  return { badge: 'bg-slate-100 text-slate-700 ring-slate-200', row: 'border-l-slate-300', panel: 'border-slate-200 bg-slate-50 text-slate-700', dot: 'bg-emerald-500' };
}

function toneClasses(tone) {
  if (tone === 'red') return 'bg-red-50 text-red-700 ring-red-100';
  if (tone === 'orange') return 'bg-orange-50 text-orange-700 ring-orange-100';
  if (tone === 'amber') return 'bg-amber-50 text-amber-700 ring-amber-100';
  if (tone === 'emerald') return 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  if (tone === 'blue') return 'bg-blue-50 text-blue-700 ring-blue-100';
  return 'bg-slate-100 text-slate-700 ring-slate-200';
}

function Badge({ children, tone = 'slate', className = '' }) {
  return <span className={cx('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ring-1', toneClasses(tone), className)}>{children}</span>;
}

function SeverityBadge({ severity }) {
  const normalized = String(severity || 'INFO').toUpperCase();
  const tone = normalized === 'CRITICAL' ? 'red' : normalized === 'WARNING' ? 'orange' : 'slate';
  return <Badge tone={tone}>{normalized}</Badge>;
}

function OutcomeBadge({ status }) {
  const normalized = String(status || 'unknown').toLowerCase();
  const tone = normalized === 'success' ? 'emerald' : ['failed', 'error', 'denied'].includes(normalized) ? 'red' : 'slate';
  return <Badge tone={tone}>{normalized}</Badge>;
}

function EvidenceSourceBadge({ event }) {
  const source = getEvidenceSource(event);
  return <Badge tone={hasSnapshot(event) ? 'emerald' : 'amber'}>{hasSnapshot(event) ? 'Snapshot' : source === 'legacy_raw_proto' ? 'Legacy' : 'Raw'}</Badge>;
}

function CategoryBadge({ category }) {
  const tone = category === 'auth' ? 'blue' : category === 'pii' ? 'red' : category === 'rbac' ? 'orange' : category === 'document' ? 'amber' : category === 'ropa' ? 'emerald' : 'slate';
  return <Badge tone={tone}>{getCategoryMeta(category).label}</Badge>;
}

function RiskBadge({ risk }) {
  return <span className={cx('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ring-1', riskClasses(risk.level).badge)}>{risk.label}</span>;
}

function getActorLabel(event) {
  return event?.actor_display_name || event?.admin_full_name || event?.actor_snapshot?.display_name || event?.admin_email || event?.admin_user_id || event?.actor_user_id || 'System / Unknown';
}

function getActorSubLabel(event) {
  return event?.admin_role || event?.actor_snapshot?.role || event?.actor_user_id || event?.admin_user_id || '—';
}

function getResourceLabel(event) {
  return event?.resource_display_name || event?.resource_id || event?.pii_record_uuid || event?.document_id || event?.consent_id || event?.view_session_id || '—';
}

function getResourceTypeLabel(event) {
  return String(event?.resource_type || inferCategory(event) || 'resource').replaceAll('_', ' ');
}

function normalizeListResponse(data) {
  if (Array.isArray(data)) return { results: data, count: data.length, limit: data.length, offset: 0, next_offset: null };
  const results = data?.results || data?.logs || data?.events || [];
  return { results, count: Number(data?.count ?? data?.total ?? results.length), limit: Number(data?.limit ?? 50), offset: Number(data?.offset ?? 0), next_offset: data?.next_offset ?? null };
}

function exportAuditCsv(events) {
  const headers = ['event_time', 'event_type', 'event_category', 'event_action', 'severity', 'outcome_status', 'actor', 'resource_type', 'resource', 'evidence_source', 'event_id'];
  const rows = events.map((event) => [event.event_time, event.event_type, inferCategory(event), event.event_action || event.action, event.severity, getOutcome(event), getActorLabel(event), getResourceTypeLabel(event), getResourceLabel(event), getEvidenceSource(event), event.event_id]);
  const escapeCsv = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function MetricTile({ title, value, subtitle, icon: Icon, tone = 'slate' }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{title}</p>
          <p className="mt-2 text-3xl font-black text-slate-950">{value}</p>
          {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
        </div>
        <div className={cx('rounded-2xl p-3 ring-1', toneClasses(tone))}><Icon className="h-5 w-5" /></div>
      </div>
    </div>
  );
}

function MiniHealth({ label, value, icon: Icon, ok, loading }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase text-slate-400">{label}</p>
        <Icon className={cx('h-4 w-4', ok ? 'text-emerald-600' : 'text-orange-600')} />
      </div>
      <p className="mt-2 text-lg font-black text-slate-950">{loading ? '—' : valueOrDash(value)}</p>
    </div>
  );
}

function HealthPanel({ health, loading, onRefresh }) {
  const statusText = health?.status || (health?.ok ? 'healthy' : 'degraded');
  const metrics = health?.metrics || {};
  const auditDbOk = health?.audit_db?.ok === true;
  const kafkaOk = health?.kafka?.ok === true;
  const pendingProducer = Number(metrics.pending_producer_failures || 0);
  const pendingConsumer = Number(metrics.pending_consumer_failures || 0);
  const tone = statusText === 'healthy' ? 'emerald' : statusText === 'warning' ? 'amber' : statusText === 'critical' ? 'red' : 'orange';

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={tone}>{loading ? 'Checking...' : statusText}</Badge>
            {(pendingProducer > 0 || pendingConsumer > 0) && <Badge tone="amber">Retry work pending</Badge>}
          </div>
          <h2 className="mt-3 text-xl font-black text-slate-950">Audit pipeline health</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Operational status for the immutable audit store, Kafka connection, and retry queues.</p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5 xl:min-w-[760px]">
          <MiniHealth label="Audit DB" value={auditDbOk ? 'OK' : 'Issue'} loading={loading} icon={Database} ok={auditDbOk} />
          <MiniHealth label="Kafka" value={kafkaOk ? 'OK' : 'Issue'} loading={loading} icon={Wifi} ok={kafkaOk} />
          <MiniHealth label="Producer queue" value={pendingProducer} loading={loading} icon={Activity} ok={pendingProducer === 0} />
          <MiniHealth label="Consumer queue" value={pendingConsumer} loading={loading} icon={AlertTriangle} ok={pendingConsumer === 0} />
          <MiniHealth label="Latest event" value={formatRelative(metrics.latest_event_ingested_at)} loading={loading} icon={Clock} ok />
        </div>
      </div>
      {(health?.audit_db?.audit_metrics_error || health?.kafka?.error) && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <p className="font-bold">Health detail</p>
          {health?.audit_db?.audit_metrics_error && <p className="mt-1 break-words">Audit metrics: {health.audit_db.audit_metrics_error}</p>}
          {health?.kafka?.error && <p className="mt-1 break-words">Kafka: {health.kafka.error}</p>}
        </div>
      )}
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4 text-xs text-slate-500">
        <span>Checked {formatRelative(health?.checked_at)}</span>
        <button type="button" onClick={onRefresh} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 font-bold text-slate-600 hover:bg-slate-50"><RefreshCw className="h-3.5 w-3.5" /> Re-check</button>
      </div>
    </section>
  );
}

function OverviewPanel({ summary, loading, pageInsights }) {
  return (
    <section className="rounded-[2rem] border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-white p-5 shadow-sm">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-100"><BarChart3 className="h-3.5 w-3.5" /> Enterprise audit overview</div>
          <h2 className="mt-3 text-xl font-black text-slate-950">Evidence-first audit operations</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Monitor authentication, RBAC, PII access, documents, ROPA changes, and pipeline reliability from one audit console.</p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:min-w-[760px]">
          <MetricTile title="Total events" value={loading ? '—' : summary?.total_events || 0} subtitle="Retained events" icon={Activity} />
          <MetricTile title="Last 24h" value={loading ? '—' : summary?.events_24h || 0} subtitle="Recent activity" icon={Clock} />
          <MetricTile title="Needs review" value={loading ? '—' : pageInsights.reviewEvents} subtitle="Current page" icon={AlertTriangle} tone={pageInsights.reviewEvents > 0 ? 'orange' : 'slate'} />
          <MetricTile title="Critical" value={loading ? '—' : pageInsights.criticalEvents} subtitle="Current page" icon={ShieldAlert} tone={pageInsights.criticalEvents > 0 ? 'red' : 'slate'} />
          <MetricTile title="V2 snapshots" value={loading ? '—' : pageInsights.snapshotEvents} subtitle="Current page" icon={ShieldCheck} tone="emerald" />
          <MetricTile title="Legacy events" value={loading ? '—' : pageInsights.legacyEvents} subtitle="Current page" icon={FileText} tone={pageInsights.legacyEvents > 0 ? 'amber' : 'slate'} />
          <MetricTile title="PII returned" value={loading ? '—' : pageInsights.returnedFields} subtitle="Fields returned" icon={Database} tone={pageInsights.returnedFields > 0 ? 'amber' : 'slate'} />
          <MetricTile title="Denied fields" value={loading ? '—' : pageInsights.deniedFields} subtitle="Policy decisions" icon={XCircle} tone={pageInsights.deniedFields > 0 ? 'orange' : 'slate'} />
        </div>
      </div>
    </section>
  );
}

function EnterpriseSelect({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100">
        {options.map((option) => <option key={`${label}-${option.value}`} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

function EnterpriseInput({ label, value, onChange, placeholder, type = 'text', mono = false }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <input type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className={cx('w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-100', mono && 'font-mono text-xs')} />
    </label>
  );
}

function ActiveFilterChips({ filters, onClearOne, onReset }) {
  const activeEntries = Object.entries(filters).filter(([key, value]) => key === 'time_preset' ? value && value !== '' : value !== '' && value !== null && value !== undefined);
  if (!activeEntries.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {activeEntries.map(([key, value]) => (
        <button key={key} type="button" onClick={() => onClearOne(key)} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-200">
          <span>{key.replaceAll('_', ' ')}: {String(value)}</span><X className="h-3.5 w-3.5" />
        </button>
      ))}
      <button type="button" onClick={onReset} className="rounded-full px-3 py-1 text-xs font-bold text-orange-700 hover:bg-orange-50">Clear all</button>
    </div>
  );
}

function FilterPanel({ filters, setFilter, setFilters, onApply, onApplyWithFilters, onReset, eventOptions }) {
  const applyTimePreset = (preset) => {
    const range = getPresetRange(preset);
    const next = {
      ...filters,
      time_preset: preset,
      ...(preset === 'custom' ? {} : range),
      ...(preset === '' ? { start_date: '', end_date: '' } : {}),
    };

    setFilters(next);

    if (preset !== 'custom') {
      onApplyWithFilters(next);
    }
  };

  const applySavedView = (quickFilter) => {
    const keepTimeWindow = {
      time_preset: filters.time_preset,
      start_date: filters.start_date,
      end_date: filters.end_date,
    };

    const next = {
      ...DEFAULT_FILTERS,
      ...keepTimeWindow,
      ...quickFilter.apply,
    };

    setFilters(next);
    onApplyWithFilters(next);
  };

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200"><SlidersHorizontal className="h-3.5 w-3.5" /> Advanced filters</div>
          <h2 className="mt-3 text-lg font-black text-slate-950">Find events quickly</h2>
          <p className="mt-1 text-sm text-slate-500">Use predefined categories and event types instead of manually searching raw event names. Time presets auto-apply and are interpreted in Asia/Kolkata.</p>
          <div className="mt-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Saved investigation views — replace event filters, keep the selected time window</p>
            <div className="flex flex-wrap gap-2">
              {QUICK_FILTERS.map((quickFilter) => {
                const Icon = quickFilter.icon;
                return (
                  <button
                    key={quickFilter.id}
                    type="button"
                    onClick={() => applySavedView(quickFilter)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-700 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                  >
                    <Icon className="h-4 w-4" />
                    {quickFilter.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {TIME_PRESETS.map((preset) => (
            <button key={preset.value || 'all'} type="button" onClick={() => applyTimePreset(preset.value)} className={cx('rounded-xl px-3 py-2 text-xs font-bold ring-1 transition', filters.time_preset === preset.value ? 'bg-orange-600 text-white ring-orange-600' : 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-50')}>{preset.label}</button>
          ))}
        </div>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="md:col-span-2">
          <label className="block">
            <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">Search</span>
            <div className="relative"><Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" /><input value={filters.search} onChange={(event) => setFilter('search', event.target.value)} placeholder="Search event id, actor, resource, action..." className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-100" /></div>
          </label>
        </div>
        <EnterpriseSelect label="Category" value={filters.event_category} onChange={(value) => setFilter('event_category', value)} options={CATEGORY_OPTIONS} />
        <EnterpriseSelect label="Event type" value={filters.event_type} onChange={(value) => setFilter('event_type', value)} options={eventOptions} />
        <EnterpriseSelect label="Severity" value={filters.severity} onChange={(value) => setFilter('severity', value)} options={SEVERITY_OPTIONS} />
        <EnterpriseSelect label="Outcome" value={filters.outcome_status} onChange={(value) => setFilter('outcome_status', value)} options={OUTCOME_OPTIONS} />
        <EnterpriseSelect label="Resource type" value={filters.resource_type} onChange={(value) => setFilter('resource_type', value)} options={RESOURCE_TYPE_OPTIONS} />
        <EnterpriseInput label="Event action" value={filters.event_action} onChange={(value) => setFilter('event_action', value)} placeholder="group_created / login_failed" />
        <EnterpriseInput label="Actor user ID" value={filters.actor_user_id} onChange={(value) => setFilter('actor_user_id', value)} placeholder="Actor UUID" mono />
        <EnterpriseInput label="Admin email — auth only" value={filters.admin_email} onChange={(value) => setFilter('admin_email', value)} placeholder="Only auth/account events" />
        <EnterpriseInput label="Resource ID" value={filters.resource_id} onChange={(value) => setFilter('resource_id', value)} placeholder="Resource identifier" mono />
        <EnterpriseInput label="Start" type="datetime-local" value={filters.start_date} onChange={(value) => { setFilter('start_date', value); setFilter('time_preset', 'custom'); }} />
        <EnterpriseInput label="End" type="datetime-local" value={filters.end_date} onChange={(value) => { setFilter('end_date', value); setFilter('time_preset', 'custom'); }} />
      </div>
      <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-5 xl:flex-row xl:items-center xl:justify-between">
        <ActiveFilterChips filters={filters} onClearOne={(key) => setFilter(key, DEFAULT_FILTERS[key] ?? '')} onReset={onReset} />
        <div className="flex flex-wrap gap-3"><button type="button" onClick={onReset} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">Reset</button><button type="button" onClick={onApply} className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700">Apply filters</button></div>
      </div>
    </section>
  );
}

function FieldDecisionPreview({ event }) {
  const fieldAccess = getEventFieldAccess(event);
  if (!fieldAccess.viewMode && !fieldAccess.purposeCode && fieldAccess.returned.length === 0 && fieldAccess.masked.length === 0 && fieldAccess.denied.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {fieldAccess.viewMode && <Badge tone="orange">Mode: {String(fieldAccess.viewMode).toUpperCase()}</Badge>}
      {fieldAccess.purposeCode && <Badge tone="slate">Purpose: {fieldAccess.purposeCode}</Badge>}
      {fieldAccess.returned.length > 0 && <Badge tone="red">Returned: {fieldAccess.returned.length}</Badge>}
      {fieldAccess.masked.length > 0 && <Badge tone="amber">Masked: {fieldAccess.masked.length}</Badge>}
      {fieldAccess.denied.length > 0 && <Badge tone="slate">Denied: {fieldAccess.denied.length}</Badge>}
    </div>
  );
}

function AuditTableRow({ event, onView }) {
  const category = inferCategory(event);
  const risk = getAccessRisk(event);
  const riskClass = riskClasses(risk.level);

  return (
    <tr className="group border-b border-slate-100 align-top hover:bg-orange-50/30">
      <td className={cx('border-l-4 px-4 py-4', riskClass.row)}><div className="min-w-[170px]"><p className="text-sm font-black text-slate-950">{formatRelative(event.event_time)}</p><p className="mt-1 text-xs text-slate-500">{formatDate(event.event_time)}</p><p className="mt-2 font-mono text-[11px] text-slate-400">{truncateMiddle(event.event_id, 10, 8)}</p></div></td>
      <td className="px-4 py-4"><div className="min-w-[260px]"><div className="flex flex-wrap gap-2"><CategoryBadge category={category} /><SeverityBadge severity={event.severity} /><OutcomeBadge status={getOutcome(event)} /><EvidenceSourceBadge event={event} /></div><p className="mt-3 text-sm font-black text-slate-950">{getEventLabel(event)}</p><p className="mt-1 text-xs text-slate-500">Action: <span className="font-semibold text-slate-700">{getEventActionLabel(event)}</span></p><FieldDecisionPreview event={event} /></div></td>
      <td className="px-4 py-4"><div className="min-w-[210px]"><div className="flex items-start gap-2"><User className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /><div className="min-w-0"><p className="truncate text-sm font-bold text-slate-900">{getActorLabel(event)}</p><p className="mt-1 break-all text-xs text-slate-500">{getActorSubLabel(event)}</p></div></div></div></td>
      <td className="px-4 py-4"><div className="min-w-[220px]"><p className="text-xs font-bold uppercase tracking-wide text-slate-400">{getResourceTypeLabel(event)}</p><p className="mt-1 break-all font-mono text-xs text-slate-700">{getResourceLabel(event)}</p></div></td>
      <td className="px-4 py-4"><div className={cx('max-w-[280px] rounded-2xl border p-3 text-sm', riskClass.panel)}><div className="flex items-start gap-2"><span className={cx('mt-1 h-2 w-2 shrink-0 rounded-full', riskClass.dot)} /><div><p className="font-black">{risk.label}</p><p className="mt-1 text-xs">{risk.message}</p></div></div></div></td>
      <td className="px-4 py-4 text-right"><button type="button" onClick={() => onView(event)} className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"><Eye className="h-4 w-4" />Evidence</button></td>
    </tr>
  );
}

function AuditMobileCard({ event, onView }) {
  const category = inferCategory(event);
  const risk = getAccessRisk(event);
  const riskClass = riskClasses(risk.level);
  return (
    <article className={cx('rounded-3xl border border-l-4 bg-white p-5 shadow-sm', riskClass.row)}>
      <div className="flex flex-wrap gap-2"><CategoryBadge category={category} /><SeverityBadge severity={event.severity} /><OutcomeBadge status={getOutcome(event)} /><EvidenceSourceBadge event={event} /></div>
      <h3 className="mt-3 text-base font-black text-slate-950">{getEventLabel(event)}</h3>
      <p className="mt-1 text-xs text-slate-500">{formatDate(event.event_time)}</p>
      <div className="mt-4 grid gap-3"><div><p className="text-xs font-bold uppercase text-slate-400">Actor</p><p className="mt-1 text-sm font-semibold text-slate-900">{getActorLabel(event)}</p></div><div><p className="text-xs font-bold uppercase text-slate-400">Resource</p><p className="mt-1 break-all font-mono text-xs text-slate-700">{getResourceLabel(event)}</p></div><div className={cx('rounded-2xl border p-3 text-sm', riskClass.panel)}><p className="font-black">{risk.label}</p><p className="mt-1 text-xs">{risk.message}</p></div></div>
      <button type="button" onClick={() => onView(event)} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"><Eye className="h-4 w-4" />View evidence</button>
    </article>
  );
}

function SidePanel({ summary, logs, onView }) {
  const needsReview = logs.filter((event) => ['critical', 'warning'].includes(getAccessRisk(event).level)).slice(0, 8);
  const categoryCounts = logs.reduce((acc, event) => { const category = inferCategory(event); acc[category] = (acc[category] || 0) + 1; return acc; }, {});
  return (
    <aside className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><h3 className="font-black text-slate-950">Needs review</h3><p className="mt-1 text-sm text-slate-500">Critical, failed, denied, or warning events from this page.</p><div className="mt-4 space-y-3">{needsReview.length === 0 ? <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-700"><div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5" /><p>No current-page events require review.</p></div></div> : needsReview.map((event) => { const risk = getAccessRisk(event); return <button key={event.event_id} type="button" onClick={() => onView(event)} className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left hover:bg-slate-50"><div className="flex items-start justify-between gap-3"><div><p className="line-clamp-1 text-sm font-bold text-slate-950">{getEventLabel(event)}</p><p className="mt-1 text-xs text-slate-500">{formatRelative(event.event_time)}</p></div><RiskBadge risk={risk} /></div><p className="mt-2 line-clamp-2 text-xs text-slate-600">{risk.message}</p></button>; })}</div></section>
      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><h3 className="font-black text-slate-950">Page category mix</h3><div className="mt-4 space-y-3">{Object.entries(categoryCounts).map(([category, count]) => <div key={category} className="flex items-center justify-between gap-3"><CategoryBadge category={category} /><span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">{count}</span></div>)}{Object.keys(categoryCounts).length === 0 && <p className="text-sm text-slate-500">No events loaded.</p>}</div></section>
      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><h3 className="font-black text-slate-950">Top event types</h3><div className="mt-4 space-y-3">{(summary?.top_event_types || []).slice(0, 8).map((item) => <div key={item.event_type} className="flex items-center justify-between gap-3"><span className="truncate text-sm text-slate-600">{String(item.event_type || '').replaceAll('_', ' ').replaceAll('.', ' › ')}</span><span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-700">{item.count}</span></div>)}{(summary?.top_event_types || []).length === 0 && <p className="text-sm text-slate-500">Summary data unavailable.</p>}</div></section>
    </aside>
  );
}

export default function AuditLogsPage() {
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, ...getPresetRange(DEFAULT_FILTERS.time_preset) }));
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ count: 0, limit: 50, offset: 0, next_offset: null });

  const eventOptions = useMemo(() => {
    const options = EVENT_CATALOG.filter((item) => !filters.event_category || item.category === filters.event_category).map((item) => ({ value: item.value, label: `${item.label} (${item.value})` }));
    return [{ value: '', label: 'All event types' }, ...options];
  }, [filters.event_category]);

  const activeFilterCount = useMemo(() => Object.entries(filters).filter(([key, value]) => key === 'time_preset' ? value && value !== '' : value !== '' && value !== null && value !== undefined).length, [filters]);

  const pageInsights = useMemo(() => logs.reduce((acc, event) => {
    const risk = getAccessRisk(event);
    const fieldAccess = getEventFieldAccess(event);
    acc.returnedFields += fieldAccess.returned.length;
    acc.deniedFields += fieldAccess.denied.length;
    if (risk.level === 'critical') acc.criticalEvents += 1;
    if (risk.level === 'critical' || risk.level === 'warning') acc.reviewEvents += 1;
    if (hasSnapshot(event)) acc.snapshotEvents += 1;
    if (getEvidenceSource(event) === 'legacy_raw_proto') acc.legacyEvents += 1;
    return acc;
  }, { reviewEvents: 0, criticalEvents: 0, returnedFields: 0, deniedFields: 0, snapshotEvents: 0, legacyEvents: 0 }), [logs]);

  const pageStart = pagination.count === 0 ? 0 : pagination.offset + 1;
  const pageEnd = Math.min(pagination.offset + pagination.limit, pagination.count);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'event_category') {
        const selectedType = EVENT_CATALOG.find((item) => item.value === next.event_type);
        if (selectedType && selectedType.category !== value && value) next.event_type = '';
      }
      return next;
    });
  }, []);

  const buildParams = useCallback((override = {}) => {
    const merged = { ...filters, ...override };
    const params = { limit: override.limit ?? pagination.limit, offset: override.offset ?? 0 };
    Object.entries(merged).forEach(([key, value]) => {
      if (key === 'time_preset') return;
      if (value !== '' && value !== null && value !== undefined) {
        params[key] = normalizeDateFilterParam(key, value);
      }
    });
    if (params.event_action && !params.action) params.action = params.event_action;
    return params;
  }, [filters, pagination.limit]);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await auditAPI.getHealth();
      setHealth(res.data);
    } catch (err) {
      setHealth(err.response?.data || { ok: false, status: 'degraded', audit_db: { ok: false }, kafka: { ok: false, error: err.message }, metrics: {} });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await auditAPI.getSummary();
      setSummary(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load audit summary.');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async (override = {}) => {
    setLoading(true);
    setError('');
    try {
      const res = await auditAPI.getLogs(buildParams(override));
      const normalized = normalizeListResponse(res.data);
      setLogs(normalized.results);
      setPagination({ count: normalized.count, limit: normalized.limit || pagination.limit, offset: normalized.offset || 0, next_offset: normalized.next_offset });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [buildParams, pagination.limit]);

  useEffect(() => {
    loadHealth();
    loadSummary();
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAll = () => { loadHealth(); loadSummary(); loadLogs({ offset: pagination.offset }); };
  const applyFilters = () => loadLogs({ offset: 0 });
  const applyFiltersWithState = (nextFilters) => loadLogs({ ...nextFilters, offset: 0 });
  const resetFilters = () => {
    const next = { ...DEFAULT_FILTERS, ...getPresetRange(DEFAULT_FILTERS.time_preset) };
    setFilters(next);
    applyFiltersWithState(next);
  };
  const nextPage = () => { if (pagination.next_offset !== null && pagination.next_offset !== undefined) loadLogs({ offset: pagination.next_offset }); };
  const prevPage = () => loadLogs({ offset: Math.max(0, pagination.offset - pagination.limit) });
  const changePageSize = (limit) => {
    setPagination((prev) => ({ ...prev, limit, offset: 0, next_offset: null }));
    loadLogs({ limit, offset: 0 });
  };

  return (
    <PermissionGate permission="search_audit_logs" showFallback>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-3"><div className="rounded-2xl bg-slate-950 p-3 text-white shadow-sm"><Activity className="h-6 w-6" /></div><div><div className="flex flex-wrap items-center gap-2"><h1 className="text-2xl font-black text-slate-950">Audit Command Center</h1><Badge tone="emerald">Schema v2 ready</Badge></div><p className="mt-1 text-sm text-slate-500">Enterprise evidence console for auth, PII, documents, RBAC, ROPA, and immutable audit snapshots.</p></div></div>
          <div className="flex flex-wrap gap-3"><button type="button" onClick={() => setShowFilters((prev) => !prev)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"><Filter className="h-4 w-4" />Filters{activeFilterCount > 0 && <span className="rounded-full bg-orange-600 px-2 py-0.5 text-xs text-white">{activeFilterCount}</span>}<ChevronDown className={cx('h-4 w-4 transition', showFilters && 'rotate-180')} /></button><button type="button" onClick={() => exportAuditCsv(logs)} disabled={logs.length === 0} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"><Download className="h-4 w-4" />Export page</button><button type="button" onClick={refreshAll} className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"><RefreshCw className={cx('h-4 w-4', (loading || summaryLoading || healthLoading) && 'animate-spin')} />Refresh</button></div>
        </div>

        {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700"><div className="flex items-start gap-3"><AlertTriangle className="mt-0.5 h-5 w-5" /><div><p className="font-bold">Could not load audit console</p><p className="mt-1 text-sm">{error}</p></div></div></div>}

        <HealthPanel health={health} loading={healthLoading} onRefresh={loadHealth} />
        <OverviewPanel summary={summary} loading={summaryLoading} pageInsights={pageInsights} />

        {showFilters && (
          <FilterPanel
            filters={filters}
            setFilter={setFilter}
            setFilters={setFilters}
            onApply={applyFilters}
            onApplyWithFilters={applyFiltersWithState}
            onReset={resetFilters}
            eventOptions={eventOptions}
          />
        )}

        <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[1fr_360px]">
          <section className="rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-4 border-b border-slate-200 p-5 xl:flex-row xl:items-center xl:justify-between"><div><h2 className="font-black text-slate-950">Audit events</h2><p className="mt-1 text-sm text-slate-500">Showing {pageStart}-{pageEnd} of {pagination.count} events.</p></div><div className="flex flex-wrap items-center gap-3"><label className="flex items-center gap-2 text-sm font-semibold text-slate-500">Page size<select value={pagination.limit} onChange={(event) => changePageSize(Number(event.target.value))} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm font-bold text-slate-700">{PAGE_SIZE_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>{loading && <span className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-100"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading</span>}</div></div>
            <div className="hidden overflow-x-auto xl:block"><table className="min-w-full text-left"><thead className="bg-slate-50"><tr><th className="px-4 py-3 text-xs font-black uppercase tracking-wide text-slate-500">Time</th><th className="px-4 py-3 text-xs font-black uppercase tracking-wide text-slate-500">Event</th><th className="px-4 py-3 text-xs font-black uppercase tracking-wide text-slate-500">Actor</th><th className="px-4 py-3 text-xs font-black uppercase tracking-wide text-slate-500">Resource</th><th className="px-4 py-3 text-xs font-black uppercase tracking-wide text-slate-500">Assessment</th><th className="px-4 py-3 text-right text-xs font-black uppercase tracking-wide text-slate-500">Action</th></tr></thead><tbody>{logs.map((event) => <AuditTableRow key={event.event_id} event={event} onView={setSelectedEvent} />)}</tbody></table></div>
            <div className="space-y-4 p-5 xl:hidden">{logs.map((event) => <AuditMobileCard key={event.event_id} event={event} onView={setSelectedEvent} />)}</div>
            {!loading && logs.length === 0 && <div className="p-12 text-center"><Search className="mx-auto h-12 w-12 text-slate-300" /><p className="mt-3 text-lg font-black text-slate-700">No audit events found</p><p className="mt-1 text-sm text-slate-500">Adjust filters, category, time window, or outcome.</p></div>}
            <div className="flex flex-col gap-3 border-t border-slate-200 p-5 md:flex-row md:items-center md:justify-between"><p className="text-sm text-slate-500">Offset {pagination.offset} · Limit {pagination.limit}</p><div className="flex gap-2"><button type="button" onClick={prevPage} disabled={pagination.offset === 0 || loading} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">Previous</button><button type="button" onClick={nextPage} disabled={pagination.next_offset === null || pagination.next_offset === undefined || loading} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">Next</button></div></div>
          </section>
          <SidePanel summary={summary} logs={logs} onView={setSelectedEvent} />
        </div>

        {selectedEvent && <AuditEvidenceDrawer key={selectedEvent.event_id} eventId={selectedEvent.event_id} event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
      </div>
    </PermissionGate>
  );
}
